import React, { useRef, useState } from 'react';
import { Dialog, DialogContent, DialogActions, Divider } from '@mui/material';
import { SBBox, SBTypography, SBIcon, SBIconButton, SBOutlinedButton } from 'modules/Commons';
import { useStyles } from "./style";
import { IPurchaseCSVErrors } from 'views/PurchaseViews/PurchaseUploaderHelper/IPurchaseCSVErrors';
import { SBTranslate } from "i18n";
import { IPurchasePlan } from "modules/Purchases/models/IPurchasePlans";
import { processCSV } from 'views/PurchaseViews/PurchaseUploaderHelper/AditiveCSVHandler';
import { ICSVModifier } from "modules/Purchases/models/ICSVaditives";
import PurchaseUploaderHelperError from './PurchaseUploaderHelperError';
import PurchaseUploaderHelperSelector from './PurchaseUploaderHelperSelector';

export enum UploaderState {
    Waiting = 'onWaiting',
    Error = 'onError',
    Finished = ''
}

export enum ImportMode {
    Incremental = 'incremental',
    Substitute = 'substitute'
}

interface ErrorModalProps {
    open: boolean;
    errors: IPurchaseCSVErrors[];
    handleClose: () => void;
    handleReUpload: () => void;
    rows: IPurchasePlan[];
    setRows: React.Dispatch<React.SetStateAction<IPurchasePlan[]>>;
    setCsvProcessed: React.Dispatch<React.SetStateAction<boolean>>;
    setModalErrors: React.Dispatch<React.SetStateAction<IPurchaseCSVErrors[]>>;
}

const ErrorModal: React.FC<ErrorModalProps> = ({
    open, errors, handleClose: originalClose, rows, setRows, setCsvProcessed, setModalErrors
}) => {
    const classes = useStyles();
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [currentState, setCurrentState] = useState<UploaderState>(UploaderState.Waiting);
    const [validData, setValidData] = useState<ICSVModifier[]>([]);
    const [importMode, setImportMode] = useState<ImportMode>(ImportMode.Incremental);



    const fetchState = (state: UploaderState) => {
        switch (state) {
            case UploaderState.Error:
                return <PurchaseUploaderHelperError errors={errors} handleReUpload={triggerFileInput} />;
            case UploaderState.Waiting:
                return <PurchaseUploaderHelperSelector onFileAccepted={handleFileAccepted} importMode={importMode} setImportMode={setImportMode} />;
            default:
                return null;
        }
    };
    const handleClose = () => {
        setModalErrors([]);
        setCurrentState(UploaderState.Waiting);
        originalClose();
    };

    const handleFileAccepted = (file: File) => {
        setCurrentState(UploaderState.Waiting);
        processCSV(file, (validData, hasCriticalErrors) => {
            if (!hasCriticalErrors) {
                batchUpdateModifiers(validData);
                setCsvProcessed(true);
                setCurrentState(UploaderState.Finished);
                handleClose();
            } else {
                setCsvProcessed(false);
                setCurrentState(UploaderState.Error);
            }
        }, setModalErrors, setValidData, rows);
    };

    const ignoreErrorsAndUpload = () => {
        if (validData.length > 0) {
            batchUpdateModifiers(validData);
            setCsvProcessed(true);
            handleClose();
        }
    };

    const handleImportCSV = (event: React.ChangeEvent<HTMLInputElement>): void => {
        const file = event.target.files ? event.target.files[0] : null;
        if (file) {
            handleFileAccepted(file);
            event.target.value = '';
        }
    };

    const triggerFileInput = () => {
        fileInputRef.current?.click();
    };

    const batchUpdateModifiers = (validData: ICSVModifier[]) => {
        const appliedMatchCounts: { [key: string]: number } = {};

        const newRows = rows.map(row => {
            const key = `${row.inventory.product.clientID}-${row.inventory.store.clientID}`;
            const matchedCsvRows = validData.filter(
                csv => String(csv.productClientID) === String(row.inventory.product.clientID) && String(csv.storeClientID) === String(row.inventory.store.clientID)
            );

            if (matchedCsvRows.length > 0) {
                const matchIndex = Math.min((appliedMatchCounts[key] || 0), matchedCsvRows.length - 1);
                let finalModifier = matchedCsvRows[matchIndex].modifier;
                
                appliedMatchCounts[key] = (appliedMatchCounts[key] || 0) + 1;
                
                if (importMode === ImportMode.Substitute) {
                    finalModifier = finalModifier - (row.recommended || 0);
                }
                return { ...row, modifier: finalModifier };
            }

            return row;
        });

        setRows(newRows);
    };

    return (
        <Dialog open={open} onClose={() => {
            if (errors.length === 0) handleClose();
        }} maxWidth="md">
            <input
                type="file"
                accept=".csv"
                onChange={handleImportCSV}
                style={{ display: 'none' }}
                ref={fileInputRef}
            />
            <Dialog open={open} onClose={handleClose} maxWidth="md">
                <SBBox className={classes.configUploaderModal}>
                    <SBBox className={classes.configUploaderSubModal}>
                        <SBBox className={classes.configUploaderMenuHeader}>
                            <SBTypography
                                mr="auto"
                                fontSize="1rem"
                                fontWeight="700"
                            >
                                {SBTranslate("uploadCSV")}
                            </SBTypography>
                            <SBIconButton onClick={handleClose}>
                                <SBIcon icon="x" />
                            </SBIconButton>
                        </SBBox>
                        <SBBox className={classes.uploaderContainer}>
                            <SBBox className={classes.uploaderContent}>
                                <DialogContent dividers>
                                    {fetchState(currentState)}
                                </DialogContent>
                                <Divider />
                                <DialogActions>

                                    {currentState === 'onError' && (
                                        <SBBox className={classes.reUploadButton}>
                                            <SBOutlinedButton
                                                onClick={ignoreErrorsAndUpload}
                                                type="submit"
                                                color="error"
                                            >
                                                <SBIcon icon="checked" color="error" />
                                                <SBTypography ml={1} className={classes.buttonFont}
                                                    style={{ textTransform: "capitalize" }}
                                                >
                                                    {SBTranslate("IgnoreErros")}
                                                </SBTypography>
                                            </SBOutlinedButton>
                                        </SBBox>
                                    )}

                                    <SBBox className={classes.reUploadButton}>
                                        <SBOutlinedButton
                                            onClick={triggerFileInput}
                                            type="submit"
                                        >
                                            <SBIcon icon="upload" color="primary" />
                                            <SBTypography ml={1} className={classes.buttonFont}
                                                style={{ textTransform: "capitalize" }}
                                            >
                                                {SBTranslate("uploadCSV")}
                                            </SBTypography>
                                        </SBOutlinedButton>
                                    </SBBox>
                                </DialogActions>
                            </SBBox>
                        </SBBox>
                    </SBBox>
                </SBBox>
            </Dialog>
        </Dialog>
    );
};

export default ErrorModal;